'use client';

import MessageCard from '@/components/Home/MessageCard';
import SecondaryFooter from '@/components/layout/SecondaryFooter';
import PaywallCard from '@/components/@generalComponents/PaywallCard';
import MessagesSlider from '@/components/@generalComponents/MessagesSlider';
import LeftSidebar from '@/components/layout/LeftSidebar';
import SortControl from '@/components/@generalComponents/SortControl';
import TagFilter, {
	extractUniqueTags,
	filterByTag,
} from '@/components/@generalComponents/TagFilter';
import Image from 'next/image';
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Footer from '@/components/layout/Footer';
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import { applyPaywall, shouldShowPaywall } from '@/utils/paywall';
import { sortMessagesByDate } from '@/utils/sort';
import { UserAuth } from '@/app/context/AuthContext';
import { excludedCategoryIds } from '@/exports/commonExports';
import {
	withEffectiveTags,
	FILTER_TAG_LABELS,
	FILTER_TAG_ORDER,
} from '@/utils/messageFilters';
import { fetchAvailableTags } from '@/utils/tagFilterApi';
import { getAuthHeaders } from '@/utils/authHeader';

export const fallbackFooterCategories = [
	{
		name: 'Apparel',
		description:
			'Search thousands of SMS messages from apparel brands for new product announcements and promotions.',
		categoryId: '6519604594c1b61a4d62db60',
		slug: 'apparel',
	},
	{
		name: 'Beauty',
		description:
			'Search thousands of SMS messages from beauty brands for new product announcements and promotions.',
		categoryId: '6519604594c1b61a4d62db66',
		slug: 'beauty',
	},
	{
		name: 'Footwear',
		description:
			'Search thousands of SMS messages from footwear brands for new product announcements and promotions.',
		categoryId: '6519604594c1b61a4d62db6c',
		slug: 'footwear',
	},
	{
		name: 'Jewelry',
		description:
			'Search thousands of SMS messages from jewelry brands for new product announcements and promotions.',
		categoryId: '6519604594c1b61a4d62db6f',
		slug: 'jewelry',
	},
	{
		name: 'Politics',
		description:
			'Search thousands of SMS messages from political candidates for announcements and events.',
		categoryId: '6519604594c1b61a4d62db72',
		slug: 'politics',
	},
];

const CategoryMessages = ({
	categoryId,
	category: cat,
	messages: msg,
	pagination: paginationProp,
}) => {
	const [category, setCategory] = useState(cat);
	const [messages, setMessages] = useState(msg || []);
	const [pagination, setPagination] = useState(paginationProp);
	const [page, setPage] = useState(null);
	const [loading, setLoading] = useState(false);
	const [sortDirection, setSortDirection] = useState('desc');
	const [selectedTag, setSelectedTag] = useState('');
	const size = 10;
	const { user, isPro } = UserAuth();
	const theme = useTheme();
	const isMd = useMediaQuery(theme.breakpoints.down('md'));

	// Chips normally come from the server's available-tags endpoint, which
	// covers this category's ENTIRE message set (not just what's loaded on
	// screen). `tagsSource` starts as 'client' so a chip list is available
	// immediately from whatever messages are already loaded, then upgrades to
	// 'server' once the available-tags call resolves. If that call - or a
	// tag-filtered messages fetch below - ever fails (e.g. the backend hasn't
	// been deployed with this support yet), we fall back to 'client' and
	// behave exactly as this page did before server-side tag filtering
	// existed: chips derived from loaded messages, filtering done in memory.
	// This keeps the page working even when frontend and backend are a
	// release apart.
	const [tagsSource, setTagsSource] = useState('client');
	const [serverTags, setServerTags] = useState([]);
	const [filteredMessages, setFilteredMessages] = useState([]);
	const [filteredPagination, setFilteredPagination] = useState(null);
	const [filteredLoading, setFilteredLoading] = useState(false);

	// Guards against out-of-order responses when the category changes quickly.
	const tagsRequestIdRef = useRef(0);
	// Guards against out-of-order responses when the selected tag (or a
	// "Load More" click while filtering) changes/fires quickly in succession.
	const filterRequestIdRef = useRef(0);

	const isFiltering = Boolean(selectedTag) && tagsSource === 'server';

	// "RCS" and "Coupons" are derived per message, the campaign tags are the
	// ones set in the admin panel. The tag filter (client or server) runs
	// first, then sort, then the paywall - in that order - so the
	// paywall behaves identically whether the filter ran on the server or
	// in memory.
	const effectiveMessages = withEffectiveTags(messages);
	const availableTags =
		tagsSource === 'server' ? serverTags : extractUniqueTags(effectiveMessages);
	const taggedMessages = isFiltering
		? withEffectiveTags(filteredMessages)
		: filterByTag(effectiveMessages, selectedTag);
	const sortedMessages = sortMessagesByDate(taggedMessages, sortDirection);
	const { visible: visibleMessages } = applyPaywall(sortedMessages);
	const activePagination = isFiltering ? filteredPagination : pagination;
	const paywalled = shouldShowPaywall({
		user,
		isPro,
		count: visibleMessages.length,
		hasNextPage: Boolean(activePagination?.nextPage),
		hasOlder: activePagination?.hasOlder === true,
	});

	// Bumped on every tier change so a slower response from the previous tier
	// can't overwrite the refreshed list.
	const tierEpochRef = useRef(0);

	const fetchCategoryMessages = async (pageNumber = 0, replace = false) => {
		const myEpoch = tierEpochRef.current;
		setLoading(true);
		try {
			const response = await axios.get(
				`https://api.tinytexts.co/api/messages/category/${categoryId}`,
				{ params: { page: pageNumber, size }, headers: await getAuthHeaders() }
			);

			const {
				category: fetchedCategory,
				messages: fetchedMessages,
				pagination: fetchedPagination,
			} = response.data;

			if (tierEpochRef.current !== myEpoch) return; // stale (tier changed)
			setCategory(fetchedCategory);
			setMessages((prevMessages) =>
				replace ? fetchedMessages || [] : [...prevMessages, ...fetchedMessages]
			);
			setPagination(fetchedPagination);

			// if (fetchedCategory?.name) {
			// 	document.title = excludedCategoryIds.includes(categoryId)
			// 		? `${fetchedCategory.name} Text Messages`
			// 		: `${fetchedCategory.name} Text Messages | Tiny Texts`;
			// } else {
			// 	document.title = 'Category Not Found';
			// }
		} catch (error) {
			if (tierEpochRef.current !== myEpoch) return; // stale (tier changed)
			console.error('Failed to fetch category messages:', error);
			document.title = 'Error - Tiny Texts';
		} finally {
			if (tierEpochRef.current === myEpoch) setLoading(false);
		}
	};

	// Fetches a page of messages carrying `tag` directly from the server
	// (across the category's whole message set), replacing (page 0) or
	// appending to (subsequent pages) the filtered list.
	const getFilteredCategoryMessages = async (tag, pageNumber = 0) => {
		const myRequestId = ++filterRequestIdRef.current;
		setFilteredLoading(true);
		try {
			const response = await axios.get(
				`https://api.tinytexts.co/api/messages/category/${categoryId}`,
				{ params: { page: pageNumber, size, tag }, headers: await getAuthHeaders() }
			);
			if (filterRequestIdRef.current !== myRequestId) return; // stale response
			const { messages: fetchedMessages, pagination: fetchedPagination } =
				response.data;
			setFilteredMessages((prev) =>
				pageNumber === 0 ? fetchedMessages || [] : [...prev, ...(fetchedMessages || [])]
			);
			setFilteredPagination(fetchedPagination || null);
			setFilteredLoading(false);
		} catch (error) {
			if (filterRequestIdRef.current !== myRequestId) return; // stale response
			console.error('Failed to fetch tag-filtered category messages:', error);
			// Resilience fallback: revert to filtering the messages already
			// loaded on the client, exactly as this page behaved before
			// server-side tag filtering existed, rather than showing an error
			// or a stuck loading state.
			setTagsSource('client');
			setFilteredMessages([]);
			setFilteredPagination(null);
			setFilteredLoading(false);
		}
	};

	const loadMoreMessages = () => {
		if (pagination?.nextPage !== undefined && pagination?.nextPage !== null) {
			setPage(pagination.nextPage);
		}
	};

	const handleLoadMore = () => {
		if (isFiltering) {
			if (filteredPagination?.nextPage != null) {
				getFilteredCategoryMessages(selectedTag, filteredPagination.nextPage);
			}
			return;
		}
		loadMoreMessages();
	};

	useEffect(() => {
		if (page === null) return; // Avoid refetching the first page passed from the server
		fetchCategoryMessages(page);
	}, [page]);

	// The server-rendered first page is always the logged-out tier. When the
	// signed-in user (or their PRO status) differs from that, reload page 0
	// with the user's token so they get the right tier.
	const authKey = `${user?.uid ?? ''}:${isPro ? 'pro' : 'free'}`;
	const loadedAuthKeyRef = useRef(':free');
	useEffect(() => {
		if (loadedAuthKeyRef.current === authKey || !categoryId) return;
		loadedAuthKeyRef.current = authKey;
		tierEpochRef.current += 1;
		setPage(null);
		fetchCategoryMessages(0, true);
		if (selectedTag && tagsSource === 'server') {
			getFilteredCategoryMessages(selectedTag, 0);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [authKey, categoryId]);

	// A new category starts unfiltered, and refreshes the server's
	// available-tags list for that category.
	useEffect(() => {
		setCategory(cat);
		setMessages(msg || []);
		setPagination(paginationProp);
		setPage(null);
		setSelectedTag('');
		setTagsSource('client');
		setServerTags([]);
		setFilteredMessages([]);
		setFilteredPagination(null);

		const myRequestId = ++tagsRequestIdRef.current;
		if (!categoryId) return;

		(async () => {
			const tags = await fetchAvailableTags({ categoryId });
			if (tagsRequestIdRef.current !== myRequestId) return; // stale (category changed again)
			if (Array.isArray(tags)) {
				setServerTags(tags);
				setTagsSource('server');
			}
			// On failure (`tags === null`) we deliberately stay in 'client'
			// mode; see the resilience comment on `tagsSource` above.
		})();
	}, [categoryId, cat, msg, paginationProp]);

	// When the selected tag changes (and the server is known to support tag
	// filtering), fetch page 0 of the filtered results and replace the
	// filtered list. Clearing the tag just stops using `filteredMessages` -
	// no fetch is needed since `messages` already holds the unfiltered list.
	useEffect(() => {
		if (!selectedTag || tagsSource !== 'server') {
			setFilteredMessages([]);
			setFilteredPagination(null);
			setFilteredLoading(false);
			return;
		}
		getFilteredCategoryMessages(selectedTag, 0);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedTag, tagsSource, categoryId]);

	return (
		<>
		<div className='flex w-full'>
			<LeftSidebar />
			{/* <Loader renderChildren={messages.length && category}> */}
			<div className='flex-1 min-w-0 overflow-x-hidden mx-auto mb-5'>
				<div className='px-4 md:px-8 mt-8 mb-4 text-center'>
					<h1 className='text-4xl font-bold text-black text-center mb-2'>
						{excludedCategoryIds.includes(categoryId)
							? category?.name
							: `${category?.name} Text Messages`}
					</h1>
					{category?.description && (
						<p className='text-lg text-gray-700 text-center mb-4'>
							{category.description}
						</p>
					)}
				</div>
				<div className='flex flex-wrap items-center gap-4 px-8 mb-6'>
					<SortControl value={sortDirection} onChange={setSortDirection} />
					<TagFilter
						tags={availableTags}
						selected={selectedTag}
						onSelect={setSelectedTag}
						labels={FILTER_TAG_LABELS}
						order={FILTER_TAG_ORDER}
						isPro={isPro}
					/>
				</div>
				{isFiltering && filteredLoading && filteredMessages.length === 0 ? (
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							width: '100%',
							py: 4,
						}}
					>
						<Typography variant='body2' color='text.secondary'>
							Loading messages for this filter…
						</Typography>
					</Box>
				) : sortedMessages?.length === 0 ? (
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							justifyContent: 'center',
							textAlign: 'center',
							width: '100%',
							py: 4,
						}}
					>
						<Image
							alt='no-result'
							src='/images/no-result-graphic.svg'
							width={400}
							height={200}
							style={{ maxWidth: '100%', height: 'auto' }}
							loading='lazy'
						/>
						<Typography
							variant='body2'
							color='text.secondary'
							sx={{
								fontWeight: 300,
								mt: 2,
								letterSpacing: 1,
							}}
						>
							{isFiltering
								? 'No messages match this filter.'
								: selectedTag
									? 'No messages loaded yet match this filter.'
									: 'No messages found for this category.'}
						</Typography>
					</Box>
				) : isMd ? (
					<div className='px-4'>
						<MessagesSlider
							messages={visibleMessages}
							paywalled={paywalled}
							fetchMoreMessages={async () => {
								if (!paywalled && activePagination?.nextPage) {
									handleLoadMore();
								}
								return [];
							}}
						/>
					</div>
				) : (
					<>
						<div className='grid w-full grid-cols-1 gap-4 p-8 mb-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4'>
							{visibleMessages.map((message) => (
								<MessageCard
									key={message?.messageId}
									message={message}
								/>
							))}
						</div>
						{paywalled && <PaywallCard />}
						{!paywalled && activePagination?.nextPage && (
							<div className='flex justify-center mt-6'>
								<button
									className={`px-4 py-2 rounded text-white focus:outline-none focus:ring-2 focus:ring-blue-300 ${
										(isFiltering ? filteredLoading : loading)
											? 'bg-gray-400 cursor-not-allowed'
											: 'bg-blue-700 hover:bg-blue-600'
									}`}
									onClick={handleLoadMore}
									disabled={isFiltering ? filteredLoading : loading}
									aria-label='Load more messages'
								>
									{(isFiltering ? filteredLoading : loading) ? 'Loading...' : 'Load More'}
								</button>
							</div>
						)}
					</>
				)}
			</div>
			{/* </Loader> */}
		</div>
		<SecondaryFooter />
		{/* {!Array.isArray(category) && category && (
			<Footer showFooter={true} categories={category ? fallbackFooterCategories: [category]} />
		)} */}
		</>
	);
};

export default CategoryMessages;
